import {
    useContext,
    type FC
} from "react";
import {
    View
} from "react-native";
import type ITraceSourcesProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    useTraceOpen
} from "../../util";
import {
    BookOpen as BookOpenIcon
} from "lucide-react-native";
import {
    openSourceUrl
} from "../../../citation/util";
import {
    TraceContext
} from "../../context";
import TraceSourceRow from "../../internal/sourceRow";
import TraceCollapsible from "../collapsible";
import TraceHead from "../../internal/head";
import TraceContent from "../content";
import TraceTrigger from "../trigger";
import TraceLine from "../line";

const TraceSources: FC<ITraceSourcesProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    isOpen: isOpenProp,
    status = "done",
    isDefaultOpen,
    onSourcePress,
    onOpenChange,
    sources = [],
    description,
    style,
    title,
    ...props
}) => {
    const traceContext = useContext(TraceContext);

    const customLocalize = customLocalizeProp ?? traceContext?.customLocalize;

    const customTheme = customThemeProp ?? traceContext?.customTheme;

    const {
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        handleOpenChange,
        isOpen
    } = useTraceOpen({
        isRunning: status === "running",
        isOpen: isOpenProp,
        isDefaultOpen,
        onOpenChange
    });

    const getTitle = (): string => {
        if(title) {
            return title;
        }

        if(status === "running") {
            return localize("searching-sources");
        }

        if(status === "error") {
            return localize("sources-failed");
        }

        return sources.length === 1 ? localize("one-source-used") : localize("x-sources-used", [
            sources.length
        ]);
    };

    const handleSourcePress = (sourceIndex: number) => {
        const source = sources[sourceIndex];

        if(!source) {
            return;
        }

        if(onSourcePress) {
            onSourcePress(source, sourceIndex);

            return;
        }

        openSourceUrl({
            source
        });
    };

    const renderBookIcon: NCoreUIKitIcon = ({
        customColor,
        color,
        size
    }) => <BookOpenIcon
        color={customColor ?? colors.content.icon[color]}
        size={size}
    />;

    const renderLine = () => {
        return <TraceLine
            customTheme={customTheme}
            description={description}
            icon={renderBookIcon}
            title={getTitle()}
            status={status}
        />;
    };

    if(status !== "done" || !sources.length) {
        return <View
            {...props}
            style={[
                style,
                stylesheet.container
            ]}
        >
            <TraceHead
                customLocalize={customLocalize}
                customTheme={customTheme}
            >
                {renderLine()}
            </TraceHead>
        </View>;
    }

    return <TraceCollapsible
        {...props}
        onOpenChange={handleOpenChange}
        customTheme={customTheme}
        isOpen={isOpen}
        style={style}
    >
        <TraceTrigger
            customLocalize={customLocalize}
            customTheme={customTheme}
        >
            {renderLine()}
        </TraceTrigger>
        <TraceContent
            customTheme={customTheme}
        >
            <View
                style={stylesheet.list}
                role="list"
            >
                {
                    sources.map((source, sourceIndex) => <TraceSourceRow
                        onPress={() => handleSourcePress(sourceIndex)}
                        key={`${source.url}-${sourceIndex}`}
                        customLocalize={customLocalize}
                        customTheme={customTheme}
                        index={sourceIndex + 1}
                        source={source}
                    />)
                }
            </View>
        </TraceContent>
    </TraceCollapsible>;
};
export default TraceSources;
